import moment from 'moment';
import Cookies from "universal-cookie";
import { DEFAULT_TIMEZONE, DAILY_OT_EXCLUDE_STATES } from "Helpers/Constants";

import React, { useContext, useEffect, useRef, useState } from 'react';
import ReactPaginate from 'react-paginate';
import {
  details, exportTimeHistory, updateCheckInOutEntry, usersAdminHistory
} from "./../../../api/clock";
// Services
import { getUserLookup } from "./../../../api/user";
import { useUser } from "Context/UserContext/useUser";
import ExportIcon from './../../../Assets/images/Export.svg';
// Images
import SortIcon from './../../../Assets/images/sort.svg';
import MultiSelectNew from "Components/MultiSelectNew";
// Components
import Header from './../../../Components/Header/Header';
import ClockDetail from './../../../Components/Modal/ClockDetail/ClockDetail';
// Context
import { AppContext } from "./../../../Context/AppContext";
// Services
import styles from './index.module.scss';
import * as XLSX from "xlsx-js-style";
import GlobalDateFilters from 'Components/GlobalDateFilters/GlobalDateFilters';

// Utility function to download Excel file
const downloadExcel = (dataSet, filename) => {
  if (!dataSet || dataSet.length === 0) return;
  
  const wb = XLSX.utils.book_new();
  
  // Process each dataset (react-data-export format: [{columns, data}])
  dataSet.forEach((sheet, index) => {
    const sheetData = [];
    
    // Add header row if columns exist
    if (sheet.columns) {
      const headerRow = sheet.columns.map(col => col.title || col.value || '');
      sheetData.push(headerRow);
    }
    
    // Add data rows
    if (sheet.data) {
      sheet.data.forEach(row => {
        const rowData = row.map(cell => {
          if (typeof cell === 'object' && cell !== null) {
            if(cell.style) {
              return { v: cell.value !== undefined ? cell.value : '', s: cell.style };
            }
            return cell.value !== undefined ? cell.value : '';
          }
          return cell !== undefined ? cell : '';
        });
        sheetData.push(rowData);
      });
    }
    
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Set column widths if provided in columns
    if (sheet.columns) {
      const colWidths = sheet.columns.map(col => {
        if (col.width) {
           // support both direct number (wpx inference usually) or object {wpx: ...} or {wch: ...}
           if (typeof col.width === 'number') return { wpx: col.width }; 
           return col.width; 
        }
        return { wch: 15 }; // Default width
      });
      ws['!cols'] = colWidths;
    }
    XLSX.utils.book_append_sheet(wb, ws, `Sheet${index + 1}`);
  });
  
  XLSX.writeFile(wb, `${filename}.xlsx`);
};

// Cell values are exact "H:MM" strings, so the footer sums minutes and formats once.
// The sign sits on the hours token only, so strip it before splitting.
const sumHours = (rows, pick) => {
  const totalMinutes = rows.reduce((acc, row) => {
    const value = pick(row);
    if (typeof value !== "string" || !value.includes(":")) return acc;
    const [hours, minutes] = value.replace("-", "").split(":").map(Number);
    const cellMinutes = (hours || 0) * 60 + (minutes || 0);
    return acc + (value.trim().startsWith("-") ? -cellMinutes : cellMinutes);
  }, 0);
  if (!totalMinutes) return "-";
  const sign = totalMinutes < 0 ? "-" : "";
  const absMinutes = Math.abs(totalMinutes);
  return `${sign}${Math.floor(absMinutes / 60)}:${String(absMinutes % 60).padStart(2, "0")}`;
};

const TimeClockHistory = ({ storageKey }) => {
  const [clockDetails, setClockDetails] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [fromDate, setFromDate] = useState(
    moment().startOf("isoWeek").subtract(1, 'days').format("ddd MMM DD, YYYY")
  );
  const [toDate, setToDate] = useState(
    moment().endOf("isoWeek").subtract(1, 'days').format("ddd MMM DD, YYYY")
  );

  const [clockDetailsList, setClockDetailsList] = useState([]);
  const [sortType, setSortType] = React.useState("desc");
  const [totalCount, setTotalCount] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [deleteClockEntryId, setDeleteClockEntryId] = useState([]);

  const [employeeList, setEmployeeList] = React.useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey));
      return Array.isArray(saved) ? [...new Set(saved.filter((id) => Number.isInteger(id) && id > 0))] : [];
    } catch {
      return [];
    }
  });
  const [appliedSelection, setAppliedSelection] = useState(false);
  const requestId = useRef(0);
  const [exportHeader, setExportHeader] = React.useState([]);
  const [exportData, setExportData] = React.useState([]);

  const { showToastMessage, setIsLoading } = useContext(AppContext);

  useEffect(() => {
    handleGetEmployeeLookup();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetEmployeeLookup = async () => {
    const response = await getUserLookup("true");

    if (response && response.status === 200) {
      setEmployeeList(response.data.data);
    } else showToastMessage(undefined, response.data.message, "error", false);
  };

  const handleEmployeeChange = (options) => {
    const ids = (options || []).map((option) => option.value);
    setSelectedEmployees(ids);
    setCurrentPage(0);
    try {
      localStorage.setItem(storageKey, JSON.stringify(ids));
    } catch {
      // The report still works when browser storage is unavailable.
    }
  };

  const handleGetClockDetailsList = async (event) => {
    event?.preventDefault();
    const id = ++requestId.current;
    const reqBody = {
      user_id: selectedEmployees.length ? selectedEmployees : 0,
      sortBy: "first_name",
      sortType,
      currentPage: selectedEmployees.length ? 0 : currentPage,
      itemsPerPage: selectedEmployees.length || 10,
      start_date: moment(fromDate).format("DD-MM-YYYY"),
      end_date: moment(toDate).format("DD-MM-YYYY"),
    };
    setIsLoading(true);
    setExportData([]);
    try {
      const [response, exported] = await Promise.all([
        usersAdminHistory(reqBody),
        exportTimeHistory(reqBody),
      ]);
      if (id !== requestId.current) return;
      if (response?.status !== 200 || exported?.status !== 200) {
        throw new Error(response?.data?.message || exported?.data?.message || "Unable to load timesheets.");
      }
      setClockDetailsList(response.data.data.rows);
      setTotalCount(response.data.data.count);
      setAppliedSelection(selectedEmployees.length > 0);
      setExportHeader(exported.data.data.header);
      setExportData([exported.data.data]);
    } catch (error) {
      if (id !== requestId.current) return;
      setClockDetailsList([]);
      setTotalCount(0);
      showToastMessage(undefined, error.message, "error", false);
    } finally {
      if (id === requestId.current) setIsLoading(false);
    }
  };

  const handlePageClick = (data) => setCurrentPage(data.selected);

  const handleClearSearch = (event) => {
    event.preventDefault();
    setFromDate(moment().startOf("isoWeek").subtract(1, 'days').format("ddd MMM DD, YYYY"));
    setToDate(moment().endOf("isoWeek").subtract(1, 'days').format("ddd MMM DD, YYYY"));
    handleEmployeeChange([]);
  };

  const handleGetClockDetails = async (event, id) => {
    event.preventDefault();

    if (id) {
      setIsLoading(true);
      const response = await details(id);

      if (response && response.status === 200) {
        setClockDetails(response.data.data);
        setShowModal(true);
      } else showToastMessage(undefined, response.data.message, "error", false);

      setIsLoading(false);
    }
  };
 
  const cookies = new Cookies();
  const timezone = cookies.get("parentStoreTimezone") ? cookies.get("parentStoreTimezone") : DEFAULT_TIMEZONE;
  const onChangeValue = async (data) => {  
    let changeRequestClockObj = { ...clockDetails };
    
    let index = clockDetails.clock_time.findIndex(
      (clockTimeObj) => clockTimeObj.id === data.id
      );
      if (index > -1) {
     
        if(data.type==="regular_hours"){
          changeRequestClockObj.clock_time[index]["regular_hours"] = parseFloat(data.regular_hours);
        } 
        if(data.type==="ot1"){
          changeRequestClockObj.clock_time[index]["ot1"] = parseFloat(data.ot1);
        }
        if(data.type==="ot2"){
          changeRequestClockObj.clock_time[index]["ot2"] = parseFloat(data.ot2);
        }

      let changeDay;
      let changeTime = moment(data.time).startOf("minute").format("HH:mm");

      const secToHours = (seconds) => {
        return Math.ceil((seconds / 60 / 60) * 100) / 100;
      }

      const calculateTimeFromInOut = () => {
        const clock_in = moment.unix(changeRequestClockObj.clock_time[index].clock_in);
        const clock_out = moment.unix(changeRequestClockObj.clock_time[index].clock_out);
        const REG_LIM = 60*60*8;
        const OT1_LIM = 60*60*4;

        changeRequestClockObj.clock_time[index]["ot1"] = 0;
        changeRequestClockObj.clock_time[index]["ot2"] = 0;

        let seconds_diff = clock_out.diff(clock_in, 'seconds');

        // For TX (and any other DAILY_OT_EXCLUDE_STATES), no daily OT bucketing.
        // All hours go to regular_hours; the 40h weekly cap is applied server-side
        // when the change is saved.
        const employeeState = clockDetails?.user?.parent_store?.state;
        if (DAILY_OT_EXCLUDE_STATES.includes(employeeState)) {
          changeRequestClockObj.clock_time[index]["regular_hours"] = secToHours(seconds_diff);
          return;
        }

        // 8 hours or less is regular time
        if (seconds_diff <= REG_LIM) {
          changeRequestClockObj.clock_time[index]["regular_hours"] = secToHours(seconds_diff);
          return;
        }
        // implied else
        changeRequestClockObj.clock_time[index]["regular_hours"] = 8
        seconds_diff -= REG_LIM;

        // 8-12 is OT1
        if (seconds_diff <= OT1_LIM) {
          changeRequestClockObj.clock_time[index]["ot1"] = secToHours(seconds_diff);
          return;
        }
        // implied else
        changeRequestClockObj.clock_time[index]["ot1"] = 4
        seconds_diff -= OT1_LIM;

        // >12 is OT2
        changeRequestClockObj.clock_time[index]["ot2"] = secToHours(seconds_diff);
      }

      if (data.type === "in") {
        changeDay = moment
          .unix(changeRequestClockObj.clock_time[index].clock_in)
          .tz(timezone)
          .format("YYYY-MM-DD");
        let time = moment
          .tz(changeDay + " " + changeTime, timezone)
          .utc()
          .unix("X");
        changeRequestClockObj.clock_time[index]["clock_in"] = time;
        calculateTimeFromInOut();
      } else if (data.type === "out") {
          changeDay = moment
          .unix(changeRequestClockObj.clock_time[index].clock_out)
          .tz(timezone)
          .format("YYYY-MM-DD");
          let time = moment
          .tz(changeDay + " " + changeTime, timezone)
          .utc()
          .unix("X");
        changeRequestClockObj.clock_time[index]["clock_out"] = time;
        calculateTimeFromInOut();
      }

      setClockDetails(changeRequestClockObj);
    }
  };

  useEffect(() => {
    handleGetClockDetailsList();
    return () => {
      requestId.current += 1;
      setIsLoading(false);
    };
  }, [sortType, currentPage, selectedEmployees, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  //Manage the edit & delete timesheet report 
  const handleChangeClockDetails = async (event) => {
    event.preventDefault();
    let flag = 0;
    let errorMsg="";
    let indexArr = ["1ˢᵗ","2ⁿᵈ","3ʳᵈ","4ᵗʰ","5ᵗʰ","6ᵗʰ","7ᵗʰ","8ᵗʰ","9ᵗʰ","10ᵗʰ"]
    const entryValidation = clockDetails.clock_time.map((item,index)=>{
      //in this condition, we are checking the check-out time must be greater than the check-in time
      if(item.clock_out!=0 && item.clock_in>item.clock_out && !flag==1){
        flag=1
        errorMsg = `${indexArr[index]} entry check-out time is must be greater than check-in time`
      }else{
        if(index+1 < clockDetails.clock_time.length && !flag==1){
          //in this condition, we are checking the check-in is greater than the above entry check out 
          if(clockDetails.clock_time[index].clock_out>clockDetails.clock_time[index+1].clock_in){
            flag=1
            errorMsg = `${indexArr[index+1]} entery check-in time must be greater than ${indexArr[index]} entry check-out time`
          }
        }
      }
    })
    if(flag===1 && !errorMsg==""){
      showToastMessage(undefined, errorMsg, "error", false);
    }else{
      const postData = {
        deleted_id:deleteClockEntryId,
        ...clockDetails
      }
      const response = await updateCheckInOutEntry(postData);
  
      if (response && response.status === 200) {
      showToastMessage(undefined, response.data.message, "success", false);
      //get updated clock details list
      handleGetClockDetailsList();
      setShowModal(false)
      } else showToastMessage(undefined, response.data.message, "error", false);
      setIsLoading(false);
    }
  };
  
  return (
    <React.Fragment>
      <Header title="Timesheet Report" />

      <div id="MainContent" className="main-content">
        <div className="row">
          <div className="col-sm-12 col-xs-12 mb-3">
            <label htmlFor="timesheet-employees">Employees</label>
            <MultiSelectNew
              inputId="timesheet-employees"
              placeholder="All employees"
              isClearable
              closeMenuOnSelect={false}
              value={selectedEmployees.map((id) => {
                const employee = employeeList.find((item) => Number(item.id) === id);
                return { value: id, label: employee ? `${employee.first_name} ${employee.last_name} (#${id})` : `Employee #${id}` };
              })}
              options={employeeList.filter((employee) => Number(employee.id) > 0).map((employee) => ({
                value: Number(employee.id),
                label: `${employee.first_name} ${employee.last_name} (#${employee.id})`,
              }))}
              onChange={handleEmployeeChange}
            />
            <small>Selection remembered for you in this browser, per store. Clear to show all.</small>
          </div>

          <div className="col-sm-11 col-xs-12 m-0 p-0">
            <GlobalDateFilters
              isDateFilter
              isSearchIcon
              isClearSearchBtn
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
              fetchData={()=>handleGetClockDetailsList()}
              handleClearSearch={handleClearSearch}
              exportData={exportData}
              exportHeader={exportHeader}
              handleExport={handleGetClockDetailsList}
              toDateLabel={"Weekly To Date"}
              fromDateLabel={"Weekly From Date"}
              key={"time-clock-report"}
            />
          </div>
          <div className="col-sm-1 col-xs-12">
            <div className="d-flex justify-content-end mt-4">
              <span 
                className={`link cursor-pointer ml-3 ${styles.searchIconWrapper}`}
                onClick={() => downloadExcel(exportData, "timeHistory")}
                style={{ cursor: 'pointer' }}
              >
                <img className={styles.searchIcon} src={ExportIcon} alt="Export" />
              </span>
            </div>
          </div>
        </div>
        <div className="table-responsive my-5">
          <table className="table">
            <thead>
              <tr>
                <th className="align-top">
                  <div
                    className="d-flex align-items-center flex-nowrap cursor-pointer"
                    onClick={(e) => {
                      sortType === "desc"
                        ? setSortType("asc")
                        : setSortType("desc");
                    }}
                  >
                    <span className="text-nowrap">Employee Name</span>
                    <span
                      className={sortType === "desc" ? styles.desc : styles.asc}
                    >
                      <img
                        className="ml-2 cursor-pointer position-absolute"
                        src={SortIcon}
                        alt="Sorting"
                      />
                    </span>
                  </div>
                </th>
                {clockDetailsList &&
                  clockDetailsList.length > 0 &&
                  clockDetailsList[0].dates.map((dateObj, index) => {
                    return (
                      <th className="text-center" key={index}>
                        <span className="d-block">
                          {dateObj.substring(0, 3)}
                        </span>
                        <span className="d-inline-block">
                          {dateObj.substring(3)}
                        </span>
                      </th>
                    );
                  })}
                <th className="text-center">Regular Hours</th>
                <th className="text-center">Total OT (1.5)</th>
                <th className="text-center">Total OT (2)</th>
                <th className="text-center">Total Hours</th>
              </tr>
            </thead>
            <tbody>
              {clockDetailsList &&
                clockDetailsList.length > 0 &&
                clockDetailsList.map((clockDetailsObj, index) => {
                  return (
                    <tr key={index}>
                      <td>
                        {clockDetailsObj.first_name} {clockDetailsObj.last_name}
                      </td>
                      {clockDetailsObj.clock_time.length > 0 &&
                        clockDetailsObj.clock_time.map(
                          (clockTimeObj, index) => {
                            return (
                              <td
                                className="text-center cursor-pointer"
                                onClick={(event) =>
                                  handleGetClockDetails(event, clockTimeObj.id)
                                }
                                key={index}
                              >
                                {clockTimeObj.duration
                                  ? clockTimeObj.duration
                                  : "-"}
                              </td>
                            );
                          }
                        )}
                      <td className="text-center">
                        {clockDetailsObj.total_regular_hours
                          ? clockDetailsObj.total_regular_hours
                          : "-"}
                      </td>
                      <td className="text-center">
                        {clockDetailsObj.total_ot1
                          ? clockDetailsObj.total_ot1
                          : "-"}
                      </td>
                      <td className="text-center">
                        {clockDetailsObj.total_ot2
                          ? clockDetailsObj.total_ot2
                          : "-"}
                      </td>
                      <td className="text-center">
                        {clockDetailsObj.total_duration
                          ? clockDetailsObj.total_duration
                          : "-"}
                      </td>
                    </tr>
                  );
                })}
              {appliedSelection && clockDetailsList.length > 1 && (
                <tr className={styles.totalsRow}>
                  <td>
                    Total (selected employees)
                  </td>
                  {clockDetailsList[0].clock_time.map((_, dayIndex) => (
                    <td className="text-center" key={dayIndex}>
                      {sumHours(
                        clockDetailsList,
                        (row) => row.clock_time[dayIndex]?.duration
                      )}
                    </td>
                  ))}
                  <td className="text-center">
                    {sumHours(clockDetailsList, (row) => row.total_regular_hours)}
                  </td>
                  <td className="text-center">
                    {sumHours(clockDetailsList, (row) => row.total_ot1)}
                  </td>
                  <td className="text-center">
                    {sumHours(clockDetailsList, (row) => row.total_ot2)}
                  </td>
                  <td className="text-center">
                    {sumHours(clockDetailsList, (row) => row.total_duration)}
                  </td>
                </tr>
              )}
              {clockDetailsList && clockDetailsList.length === 0 && (
                <tr>
                  <td className="text-center" colSpan="8">
                    <span className="no-data-message">No data available.</span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {!appliedSelection && totalCount > 10 && (
            <ReactPaginate
              previousLabel={"‹"}
              nextLabel={"›"}
              pageCount={parseInt(Math.ceil(totalCount / 10))}
              onPageChange={handlePageClick}
              containerClassName={"pagination"}
            />
          )}
        </div>
      </div>

      <ClockDetail
        showModal={showModal}
        clockDetails={clockDetails}
        handleCloseModal={(event) => {
          event.preventDefault();
          setShowModal(false);
          setDeleteClockEntryId([])
        }}
        isEditable={true}
        isDeleteable={true}
        handleSubmitClick={handleChangeClockDetails}
        onChangeValue={(data)=>onChangeValue(data)}
        deleteDataArray={(data)=>setDeleteClockEntryId(data)}
        handleRemove={(data)=>setClockDetails({...clockDetails,clock_time:data})}
      />
    </React.Fragment>
  );
};

const TimesheetReport = () => {
  const { userState } = useUser();
  const userId = userState?.userProfile?.id;
  const storeId = localStorage.getItem("selectedStore");
  if (!userId || !storeId) return null;
  const storageKey = `timesheet-employees:${userId}:${storeId}`;
  return <TimeClockHistory key={storageKey} storageKey={storageKey} />;
};

export default TimesheetReport;
